import React, { useState, useEffect, useMemo } from 'react';
import { InventoryService, InventoryCategoryService } from '../services/api';
import Modal from './common/Modal';
import ConfirmationModal from './common/ConfirmationModal';
import Swal from 'sweetalert2';

import { exportToExcel } from '../utils/exportHelper';

const InventoryList = () => {
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'categories'
  const [inventory, setInventory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';

  // Item Modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    category_id: '',
    quantity: '',
    description: '',
    assigned_to: ''
  });

  // Category Modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catFormData, setCatFormData] = useState({
    name: '',
    type: 'Inventory'
  });

  // Stats
  const [stats, setStats] = useState({ total_items: 0, assets: 0, low_stock: 0 });

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    id: null,
    name: '',
    type: 'item' // 'item' or 'category'
  });

  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      const matchesSearch = item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !filterCategory || item.category_id === parseInt(filterCategory);
      const matchesType = !filterType || item.category_rel?.type === filterType;

      return matchesSearch && matchesCategory && matchesType;
    });
  }, [inventory, searchTerm, filterCategory, filterType]);

  const handleExport = () => {
    if (activeTab === 'items') {
      if (filteredInventory.length === 0) {
        Swal.fire('No Data', 'No items to export.', 'info');
        return;
      }
      const exportData = filteredInventory.map(item => ({
        'Item Name': item.item_name,
        'Category': item.category_rel?.name || 'Uncategorized',
        'Type': item.category_rel?.type || 'Inventory',
        'Quantity': item.quantity,
        'Assigned To': item.assigned_to || 'In Store',
        'Description': item.description || '-'
      }));
      exportToExcel(exportData, `Inventory_Items_Export_${new Date().toISOString().split('T')[0]}`);
    } else {
      if (filteredCategories.length === 0) {
        Swal.fire('No Data', 'No categories to export.', 'info');
        return;
      }
      const exportData = filteredCategories.map(cat => ({
        'Category Name': cat.name,
        'Type': cat.type,
        'Items Count': cat.items_count
      }));
      exportToExcel(exportData, `Inventory_Categories_Export_${new Date().toISOString().split('T')[0]}`);
    }
  };


  const filteredCategories = useMemo(() => {
    if (activeTab !== 'categories') return categories;
    return categories.filter(cat =>
      cat.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm, activeTab]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [invRes, catRes] = await Promise.all([
        InventoryService.getAll(),
        InventoryCategoryService.getAll()
      ]);
      setInventory(invRes.data);
      setCategories(catRes.data);
      calculateStats(invRes.data);
    } catch (error) {
      console.error("Error loading inventory data", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total_items = data.length;
    const assets = data.filter(i => i.category_rel?.type === 'Asset').length;
    const low_stock = data.filter(i => parseInt(i.quantity) < 5 && i.category_rel?.type === 'Inventory').length;
    setStats({ total_items, assets, low_stock });
  };

  // Item Handlers
  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name || '',
      category_id: item.category_id || '',
      quantity: item.quantity || '',
      description: item.description || '',
      assigned_to: item.assigned_to || ''
    });
    setShowModal(true);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editingItem) {
        await InventoryService.update(editingItem.id, formData);
        Swal.fire({ icon: 'success', title: 'Updated', text: 'Item updated successfully!', timer: 1500 });
      } else {
        await InventoryService.create(formData);
        Swal.fire({ icon: 'success', title: 'Created', text: 'Item added to inventory!', timer: 1500 });
      }
      setShowModal(false);
      loadAllData();
    } catch (error) {
      console.error("Error saving item", error);
    } finally {
      setSaving(false);
    }
  };

  // Category Handlers
  const handleEditCat = (cat) => {
    setEditingCat(cat);
    setCatFormData({
      name: cat.name || '',
      type: cat.type || 'Inventory'
    });
    setShowCatModal(true);
  };

  const handleSubmitCat = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editingCat) {
        await InventoryCategoryService.update(editingCat.id, catFormData);
        Swal.fire({ icon: 'success', title: 'Updated', text: 'Category updated successfully!', timer: 1500 });
      } else {
        await InventoryCategoryService.create(catFormData);
        Swal.fire({ icon: 'success', title: 'Created', text: 'Category created!', timer: 1500 });
      }
      setShowCatModal(false);
      loadAllData();
    } catch (error) {
      console.error("Error saving category", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id, name, type) => {
    setConfirmModal({
      show: true,
      id: id,
      name: name,
      type: type
    });
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (confirmModal.type === 'item') {
        await InventoryService.delete(confirmModal.id);
      } else {
        await InventoryCategoryService.delete(confirmModal.id);
      }
      setConfirmModal({ show: false, id: null, name: '', type: 'item' });
      loadAllData();
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Record removed successfully.', timer: 1500 });
    } catch (error) {
      console.error("Error deleting", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="fw-bold">Inventory & Assets</h2>
          <div className="d-flex gap-3 mt-2">
            <button
              className={`btn btn-sm rounded-pill px-4 ${activeTab === 'items' ? 'btn-primary' : 'btn-light text-muted'}`}
              onClick={() => setActiveTab('items')}
            >
              Inventory Items
            </button>
            <button
              className={`btn btn-sm rounded-pill px-4 ${activeTab === 'categories' ? 'btn-primary' : 'btn-light text-muted'}`}
              onClick={() => setActiveTab('categories')}
            >
              Categories
            </button>
          </div>
        </div>
        <div className="d-flex gap-2">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => {
          if (activeTab === 'items') {
            setEditingItem(null);
            setFormData({ item_name: '', category_id: '', quantity: '', description: '', assigned_to: '' });
            setShowModal(true);
          } else {
            setEditingCat(null);
            setCatFormData({ name: '', type: 'Inventory' });
            setShowCatModal(true);
          }
        }}>
          <i className="bi bi-plus-lg"></i> {activeTab === 'items' ? 'Add Item' : 'Add Category'}
        </button>
        </div>
      </div>

      {activeTab === 'items' && (
        <>
          <div className="stats-grid mb-5">
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-muted mb-1 small fw-bold">Total Items</p>
                <h3 className="mb-0">{stats.total_items}</h3>
              </div>
              <div className="stat-icon bg-blue-light text-primary"><i className="bi bi-box-seam"></i></div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-muted mb-1 small fw-bold">Fixed Assets</p>
                <h3 className="mb-0">{stats.assets}</h3>
              </div>
              <div className="stat-icon bg-dark-light text-dark"><i className="bi bi-laptop"></i></div>
            </div>
            <div className="stat-card">
              <div className="stat-info">
                <p className="text-muted mb-1 small fw-bold">Low Stock</p>
                <h3 className="mb-0 text-danger">{stats.low_stock}</h3>
              </div>
              <div className="stat-icon bg-red-light text-danger"><i className="bi bi-exclamation-triangle-fill"></i></div>
            </div>
          </div>

          <div className="card-box shadow-sm border">
            <div className="section-header px-4 pt-4 d-flex justify-content-between align-items-center mb-3 flex-wrap-mobile">
              <h5 className="mb-0 fw-bold">Inventory Records</h5>
              <div className="d-flex align-items-center gap-3 flex-wrap-mobile">
                <div className="position-relative w-100">
                  <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
                  <input
                    type="text"
                    className="form-control rounded-pill ps-5 bg-light border-0"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ height: '40px' }}
                  />
                </div>

                <select
                  className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
                  style={{ height: '40px' }}
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>

                <select
                  className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
                  style={{ height: '40px' }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Inventory">Inventory</option>
                  <option value="Asset">Asset</option>
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4">Item Name</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Stock</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">Loading inventory...</td></tr>
                  ) : filteredInventory.length > 0 ? (
                    filteredInventory.map((item) => (
                      <tr key={item.id}>
                        <td className="ps-4">
                          <span className="fw-bold text-dark">{item.item_name}</span>
                          {item.description && <small className="text-muted d-block">{item.description}</small>}
                        </td>
                        <td>{item.category_rel?.name || 'Uncategorized'}</td>
                        <td>
                          <span className={`badge rounded-pill fw-normal px-3 py-2 ${item.category_rel?.type === 'Asset' ? 'bg-indigo-subtle text-indigo' : 'bg-light text-dark border'}`}>
                            {item.category_rel?.type || 'Inventory'}
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold">{item.quantity}</span>
                          <span className="small text-muted d-block">{item.assigned_to ? `@ ${item.assigned_to}` : 'In Store'}</span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button className="btn btn-icon btn-light rounded-circle text-primary" onClick={() => handleEditItem(item)}><i className="bi bi-pencil-fill"></i></button>
                            <button className="btn btn-icon btn-light rounded-circle text-danger" onClick={() => handleDeleteClick(item.id, item.item_name, 'item')}><i className="bi bi-trash-fill"></i></button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">No items found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'categories' && (
        <div className="card-box shadow-sm border">
          <div className="section-header px-4 pt-4 d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">Categories</h5>
            <div className="position-relative">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '250px', height: '40px' }}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Category Name</th>
                  <th>Type</th>
                  <th className="text-center">Items Count</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className="text-center py-5 text-muted">Loading categories...</td></tr>
                ) : filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <tr key={cat.id}>
                      <td className="ps-4 fw-bold">{cat.name}</td>
                      <td>
                        <span className={`badge rounded-pill fw-normal px-3 py-2 ${cat.type === 'Asset' ? 'bg-indigo-subtle text-indigo' : 'bg-light text-dark border'}`}>
                          {cat.type}
                        </span>
                      </td>
                      <td className="text-center">{cat.items_count}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button className="btn btn-icon btn-light rounded-circle text-primary" onClick={() => handleEditCat(cat)}><i className="bi bi-pencil-fill"></i></button>
                          <button className="btn btn-icon btn-light rounded-circle text-danger" onClick={() => handleDeleteClick(cat.id, cat.name, 'category')} disabled={cat.items_count > 0}><i className="bi bi-trash-fill"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className="text-center py-5 text-muted">No categories found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Item Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title={editingItem ? "Edit Item" : "Add Inventory Item"}>
        <form onSubmit={handleSubmitItem}>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Item Name</label>
            <input type="text" className="form-control" value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} required />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Category</label>
              <select className="form-select" value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })} required>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Quantity</label>
              <input type="number" className="form-control" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} required />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Assigned To (Optional)</label>
            <input type="text" className="form-control" value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} placeholder="Lab 1, Principal Office, etc." />
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Description</label>
            <textarea className="form-control" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (editingItem ? 'Update' : 'Save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal show={showCatModal} onClose={() => setShowCatModal(false)} title={editingCat ? "Edit Category" : "Add category"}>
        <form onSubmit={handleSubmitCat}>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Category Name</label>
            <input type="text" className="form-control" value={catFormData.name} onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Type</label>
            <select className="form-select" value={catFormData.type} onChange={(e) => setCatFormData({ ...catFormData, type: e.target.value })} required>
              <option value="Inventory">Inventory (Consumables)</option>
              <option value="Asset">Asset (Fixed Assets)</option>
            </select>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowCatModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (editingCat ? 'Update' : 'Save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmationModal
        show={confirmModal.show}
        title={`Delete ${confirmModal.type}`}
        message={`Are you sure you want to delete "${confirmModal.name}"?`}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmModal({ show: false, id: null, name: '', type: 'item' })}
        loading={deleting}
      />
    </div>
  );
};

export default InventoryList;
